/**
 * Card Availability Section - Shows per-edition inventory, deck usage, and net availability
 *
 * Replaces CardLocationsSection and CrossDeckUsageSection with a unified table
 * showing inventory vs. deck assignment per edition, with green/red availability indicator.
 *
 * Only active decks reserve copies. Brewing decks and maybeboards are shown in
 * parentheses and never count against availability.
 *
 * Used in all card details panels (Collection, Deck, List, Starred, Set).
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { IoChevronDown, IoChevronForward } from 'react-icons/io5';
import { Link } from 'react-router-dom';

import CardHoverPreview from '@/components/CardHoverPreview/CardHoverPreview';
import useCardAvailability, { type EditionAvailability } from '@/hooks/useCardAvailability';
import { cardIndexByIdAtom } from '@/services/cardDatabase';
import type { DeckUsageKind } from '@/state/cardAvailability';
import type { ImageUris } from '@/types/card';
import { useAtomValue } from 'jotai';

export interface CardAvailabilitySectionProps {
  oracleId: string | undefined;
  cardName: string | undefined;
  /** The scryfall_id of the edition currently being viewed — highlighted in the table */
  selectedScryfallId?: string;
}

function getAvailabilityColor(available: number): string {
  if (available > 0) return 'text-emerald-400';
  if (available < 0) return 'text-red-400';
  return 'text-ash';
}

function getTotalAvailabilityColor(available: number): string {
  if (available > 0) return 'text-emerald-300';
  if (available < 0) return 'text-red-300';
  return 'text-silver';
}

function formatAvailable(n: number): string {
  if (n > 0) return `+${n}`;
  return String(n);
}

const USAGE_ROW_STYLES: Record<DeckUsageKind, string> = {
  committed: 'text-ash',
  planned: 'text-amber-400/60',
  maybeboard: 'text-ash/50',
};

const USAGE_ROW_TITLES: Record<DeckUsageKind, string> = {
  committed: 'Active deck — copies are reserved',
  planned: 'Brewing deck — copies are not reserved',
  maybeboard: 'Maybeboard — copies are not reserved',
};

function EditionRow({ edition, cardName, isActive }: { edition: EditionAvailability; cardName: string; isActive: boolean }): ReactElement {
  const [isExpanded, setIsExpanded] = useState(true);
  const cardsById = useAtomValue(cardIndexByIdAtom);
  const card = cardsById.get(edition.scryfallId);
  const imageUris = card?.image_uris as Partial<ImageUris> | null;
  const hasDeckUsages = edition.deckUsages.length > 0;

  return (
    <>
      <tr
        className={clsx('group cursor-default', isActive ? 'bg-sky-950/40' : 'hover:bg-obsidian/50')}
        onClick={hasDeckUsages ? () => setIsExpanded((v) => !v) : undefined}
      >
        {/* Edition */}
        <td className="py-1.5 pr-2 pl-1">
          <CardHoverPreview imageUris={imageUris} name={cardName}>
            <div className="flex items-center gap-1.5">
              {hasDeckUsages && (
                <button
                  type="button"
                  className="text-ash hover:text-silver shrink-0 p-0.5"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded((v) => !v);
                  }}
                  aria-label={isExpanded ? 'Collapse deck details' : 'Expand deck details'}
                >
                  {isExpanded ? <IoChevronDown className="h-3 w-3" /> : <IoChevronForward className="h-3 w-3" />}
                </button>
              )}
              {!hasDeckUsages && <span className="inline-block w-4" />}
              <span className="text-silver font-mono text-xs uppercase">
                {edition.setCode} {edition.collectorNumber}
              </span>
            </div>
          </CardHoverPreview>
        </td>
        {/* Inventory */}
        <td className="text-silver px-2 py-1.5 text-right text-sm tabular-nums">
          {edition.inventoryQty > 0 ? edition.inventoryQty : <span className="text-ash">—</span>}
        </td>
        {/* Decks */}
        <td className="text-silver px-2 py-1.5 text-right text-sm tabular-nums">
          {edition.committedQty > 0 ? edition.committedQty : <span className="text-ash">—</span>}
          {edition.plannedQty > 0 && (
            <span className="text-amber-400/70" title={`${edition.plannedQty} planned in brewing decks — not reserved`}>
              {' '}
              ({edition.plannedQty})
            </span>
          )}
          {edition.maybeboardQty > 0 && (
            <span className="text-ash/60" title={`${edition.maybeboardQty} in maybeboards — not reserved`}>
              {' '}
              ({edition.maybeboardQty})
            </span>
          )}
        </td>
        {/* Available */}
        <td className={clsx('py-1.5 pr-1 pl-2 text-right text-sm font-medium tabular-nums', getAvailabilityColor(edition.available))}>
          {formatAvailable(edition.available)}
        </td>
      </tr>
      {/* Deck sub-rows */}
      {hasDeckUsages &&
        isExpanded &&
        edition.deckUsages.map((usage) => (
          <tr
            key={`${usage.deckId}-${usage.kind}`}
            className={clsx('text-xs', USAGE_ROW_STYLES[usage.kind])}
            title={usage.kind === 'committed' ? undefined : USAGE_ROW_TITLES[usage.kind]}
          >
            <td className="py-0.5 pr-2 pl-8" colSpan={2}>
              <Link to={`/decks/${usage.deckId}`} className="hover:text-silver inline-flex items-center gap-1.5 transition-colors">
                <span>└ {usage.deckName}</span>
                {usage.kind === 'planned' && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400/70" aria-hidden="true" />}
              </Link>
            </td>
            <td className="px-2 py-0.5 text-right tabular-nums">{usage.kind === 'committed' ? usage.qty : `(${usage.qty})`}</td>
            <td />
          </tr>
        ))}
    </>
  );
}

function SkeletonRows(): ReactElement {
  return (
    <>
      {[1, 2, 3].map((i) => (
        <tr key={i}>
          <td className="py-1.5 pr-2 pl-1">
            <div className="bg-storm h-4 w-16 animate-pulse rounded" />
          </td>
          <td className="px-2 py-1.5">
            <div className="bg-storm ml-auto h-4 w-6 animate-pulse rounded" />
          </td>
          <td className="px-2 py-1.5">
            <div className="bg-storm ml-auto h-4 w-6 animate-pulse rounded" />
          </td>
          <td className="py-1.5 pr-1 pl-2">
            <div className="bg-storm ml-auto h-4 w-8 animate-pulse rounded" />
          </td>
        </tr>
      ))}
    </>
  );
}

export default function CardAvailabilitySection({
  oracleId,
  cardName,
  selectedScryfallId,
}: CardAvailabilitySectionProps): ReactElement | null {
  const { editions, totals, isLoading } = useCardAvailability(oracleId, cardName);

  if (!isLoading && editions.length === 0) {
    return (
      <section className="px-6 pb-6">
        <div className="bg-slate/50 border-storm rounded-xl border p-5">
          <h3 className="text-ash mb-2 text-xs font-medium tracking-wider uppercase">Availability</h3>
          <p className="text-ash text-sm">Not in collection or decks</p>
        </div>
      </section>
    );
  }

  return (
    <section className="px-6 pb-6">
      <div className="bg-slate/50 border-storm rounded-xl border p-5">
        <h3 className="text-ash mb-3 text-xs font-medium tracking-wider uppercase">
          Availability
          {!isLoading && editions.length > 0 && (
            <span className="text-ash ml-2 font-normal tracking-normal normal-case">
              {totals.inventory} owned, {totals.committed} in decks
              {totals.planned > 0 && <span className="text-amber-400/70"> · {totals.planned} planned</span>}
            </span>
          )}
        </h3>
        <table className="w-full table-fixed text-left">
          <thead>
            <tr className="text-ash border-storm border-b text-xs">
              <th className="pr-2 pb-1 pl-1 font-medium">Edition</th>
              <th className="w-12 px-2 pb-1 text-right font-medium">Inv.</th>
              <th
                className="w-24 px-2 pb-1 text-right font-medium"
                title="Copies in active decks. Parentheses are brewing decks (amber) and maybeboards — neither reserves a copy."
              >
                Decks
              </th>
              <th className="w-14 pr-1 pb-1 pl-2 text-right font-medium">Avail.</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <SkeletonRows />
            ) : (
              <>
                {editions.map((edition) => (
                  <EditionRow
                    key={edition.scryfallId}
                    edition={edition}
                    cardName={cardName ?? ''}
                    isActive={edition.scryfallId === selectedScryfallId}
                  />
                ))}
                {/* Totals row */}
                {editions.length > 0 && (
                  <tr className="border-storm border-t font-semibold">
                    <td className="text-silver pt-1.5 pr-2 pl-1 text-xs uppercase">Total</td>
                    <td className="text-silver px-2 pt-1.5 text-right text-sm tabular-nums">{totals.inventory}</td>
                    <td className="text-silver px-2 pt-1.5 text-right text-sm tabular-nums">
                      {totals.committed}
                      {totals.planned > 0 && <span className="font-normal text-amber-400/70"> ({totals.planned})</span>}
                      {totals.maybeboard > 0 && <span className="text-ash/60 font-normal"> ({totals.maybeboard})</span>}
                    </td>
                    <td
                      className={clsx(
                        'pt-1.5 pr-1 pl-2 text-right text-sm font-bold tabular-nums',
                        getTotalAvailabilityColor(totals.available),
                      )}
                    >
                      {formatAvailable(totals.available)}
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
